import React, { useRef } from "react";
import { _goToRoot, _goToBack, _goToHomeStack } from "router/helper";

import { View, TextInput, StatusBar, Platform } from "react-native";
import {
  Text,
  Toast,
  Input,
  Logout,
  Button,
  TextArea,
  Separator,
  InputLabel,
  CityPicker,
  AvatarPicker,
  HeaderLayout,
  ListContainer,
  ProvincePicker,
} from "component";

import { useForm, Controller } from "react-hook-form";
import Clipboard from "@react-native-clipboard/clipboard";

import { css } from "style";
import { mq, isEqualObjects, sn, dataUrlToFile } from "utils";
import { colors, patterns, sizes, strings } from "asset/constant";

import { useEditUser } from "client/api";

interface UserInfoFormType {
  name?: string;
  avatar?: AvatarType;
  shopName: string;
  phone?: string;
  province?: LocationT;
  city?: LocationT;
  address?: string;
}

export default function UserInfo(props: { user?: UserT; mobile?: string }) {
  const { user, mobile } = props;

  const shopNameInput = useRef<TextInput>();
  const phoneInput = useRef<TextInput>();

  const {
    watch,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<UserInfoFormType>({
    defaultValues: { ...user, avatar: { uri: user?.avatar } },
  });

  const _onNameSubmitEditing = () => shopNameInput?.current?.focus();
  const _onShopNameSubmitEditing = () => phoneInput?.current?.focus();

  const editUserMut = useEditUser();

  const _onSubmit = () => {
    const { name, province, city, address, shopName, avatar, phone } =
      getValues();

    if (user && isEqualObjects(getValues(), user)) {
      _goToBack();
      return;
    }

    const avatarFile =
      avatar?.uri?.includes("data:image") &&
      dataUrlToFile(avatar?.uri, `profile${Date.now()}`);

    const postData = new FormData();
    postData.append("name", name);
    postData.append("province_id", province?.id as any);
    postData.append("city_id", city?.id as any);
    phone && postData.append("phone", phone);
    address && postData.append("address", address);
    shopName && postData.append("store_name", shopName);

    if (Platform.OS === "web") {
      avatarFile && postData.append("avatar", avatarFile, avatarFile?.name);
    }

    if (Platform.OS === "android") {
      avatar?.uri !== user?.avatar && postData.append("avatar", avatar as any);
    }

    editUserMut.mutate(postData, {
      onSuccess: () => (user ? _goToBack() : _goToHomeStack()),
    });
  };

  const _selectCityWarn = () =>
    Toast.warning({ message: strings.please_pick_province });

  const _onShareCode = () => {
    // Share.open({
    //   title: strings.share,
    //   subject: `کد بنکداری ${user?.name}:${"\n"}`,
    //   message: user?.code,
    // });
  };

  const _onCopyCode = () => {
    if (user?.code) {
      Clipboard.setString(user?.code);
      Toast.success({ message: "کد کپی شد" });
    }
  };

  return (
    <>
      <StatusBar backgroundColor={colors.bg.screen} barStyle={"dark-content"} />

      <HeaderLayout>
        <View style={{ height: "100%", justifyContent: "center" }}>
          {user && (
            <Button
              onPress={_goToBack}
              circle={mq(45)}
              bgColor={colors.bg.light}
              absolute={{ left: mq(10) }}
              icon={{ name: "left", color: colors.text.normal, size: mq(18) }}
            />
          )}

          {user && (
            <Button
              onPress={_goToRoot}
              circle={mq(45)}
              bgColor={colors.bg.light}
              absolute={{ right: mq(10) }}
              icon={{ name: "home", color: colors.text.normal, size: mq(18) }}
            />
          )}

          {!user && (
            <Logout
              renderButton={(logout) => (
                <Button
                  onPress={logout}
                  width={mq(80)}
                  bgColor={colors.bg.light}
                  absolute={{ right: mq(10) }}
                  title={{
                    value: strings.logout,
                    weight: "bold",
                    color: colors.danger,
                  }}
                  icon={{
                    name: "logout",
                    size: mq(18),
                    color: colors.danger,
                  }}
                />
              )}
            />
          )}
        </View>
      </HeaderLayout>

      <ListContainer
        contentContainerStyle={{
          paddingHorizontal: sn(sizes.space, sizes.space, "10%", "20%"),
        }}
      >
        {!user?.code && (
          <View
            style={{
              margin: 12,
              padding: 12,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text weight="extraBold">لطفا ثبت نام خود را تکمیل کنید</Text>
          </View>
        )}

        <Controller
          name="avatar"
          control={control}
          render={({ field: { value, onChange } }) => (
            <AvatarPicker avatar={value} onChange={onChange} />
          )}
        />

        {user?.code && (
          <View
            style={{
              height: 60,
              marginBottom: 25,
              ...css.rRowCenterAlign,
              borderRadius: sizes.radius.lg,
              borderColor: colors.border.normal,
              backgroundColor: colors.bg.element,
              ...css.shadowForWeb(5),
              ...Platform.select({
                web: {
                  paddingLeft: 5,
                  paddingRight: 15,
                },
                native: {
                  paddingLeft: 15,
                  paddingRight: 5,
                },
              }),
            }}
          >
            <Text size={"xl"} weight="extraBold" color={colors.text.gray}>
              {strings.wholesaler_code}:
            </Text>

            <View style={{ marginRight: mq(10), top: mq(2) }}>
              <Text
                lang="en"
                size={"xxl"}
                weight="extraBold"
                color={colors.theme}
                style={{ letterSpacing: mq(1.2) }}
              >
                {user.code}
              </Text>
            </View>
            <View style={css.container} />
            <Separator
              axis="y"
              weight={2}
              size={"60%"}
              color={colors.text.placeholder + "55"}
            />
            <Button
              onPress={_onCopyCode}
              bgColor={colors.transparent}
              circle={mq(45)}
              style={{ marginLeft: mq(5) }}
              icon={{
                type: "ion",
                name: "copy-outline",
                color: colors.theme,
                size: mq(24),
              }}
            />
          </View>
        )}

        <InputLabel label={strings.mobile} desc="(غیر قابل ویرایش)" />
        <Input
          value={mobile}
          editable={false}
          placeholder={strings.mobile}
          icon={{ name: "mobile1" }}
        />

        <InputLabel label={strings.full_name} />
        <Controller
          name="name"
          control={control}
          rules={{ required: { value: true, message: strings.fill_input } }}
          render={({ field: { value, onChange } }) => (
            <Input
              value={value}
              onChangeText={onChange}
              placeholder={strings.insert_full_name}
              icon={{ name: "user" }}
              error={errors.name?.message as string}
              onSubmitEditing={_onNameSubmitEditing}
            />
          )}
        />

        <InputLabel label={strings.shop_name} />
        <Controller
          name="shopName"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Input
              value={value}
              onChangeText={onChange}
              placeholder={strings.insert_shop_name}
              icon={{ name: "isv" }}
              error={errors.shopName?.message as string}
              inputRef={shopNameInput}
              onSubmitEditing={_onShopNameSubmitEditing}
            />
          )}
        />

        <InputLabel label={strings.phone} />
        <Controller
          name="phone"
          control={control}
          rules={{
            pattern: { value: patterns.phone, message: strings.invalid_mobile },
          }}
          render={({ field: { value, onChange } }) => (
            <Input
              value={value}
              onChangeText={onChange}
              placeholder={strings.insert_phone}
              keyboardType="phone-pad"
              maxLength={11}
              inputRef={phoneInput}
              icon={{ name: "phone" }}
              error={errors.phone?.message as string}
            />
          )}
        />

        <InputLabel label={strings.province} />
        <Controller
          name="province"
          control={control}
          rules={{ required: { value: true, message: strings.fill_input } }}
          render={({ field: { value, onChange } }) => (
            <ProvincePicker
              value={value}
              onSelect={(val) => {
                setValue("city", undefined);
                onChange(val);
              }}
              error={errors.province?.message as string}
            />
          )}
        />

        <InputLabel label={strings.city} />
        <Controller
          name="city"
          control={control}
          rules={{ required: { value: true, message: strings.fill_input } }}
          render={({ field: { value, onChange } }) => (
            <CityPicker
              onPress={watch().province ? undefined : _selectCityWarn}
              id={watch().province?.id}
              value={value}
              onSelect={onChange}
              error={errors.city?.message as string}
            />
          )}
        />

        <InputLabel label={strings.address} />
        <Controller
          name="address"
          control={control}
          render={({ field: { value, onChange } }) => (
            <TextArea
              value={value}
              onChange={onChange}
              icon={{ type: "materialCommunity", name: "format-align-right" }}
              placeholder={strings.insert_address}
              onSubmitEditing={handleSubmit(_onSubmit)}
            />
          )}
        />
        <View style={{ height: mq(30) }} />
      </ListContainer>
      <View
        style={{
          alignSelf: "center",
          width: sn("93%", "93%", "80%", "60%"),
          justifyContent: "flex-start",
        }}
      >
        <Button
          loading={editUserMut.isLoading}
          absolute={{ bottom: mq(0) }}
          onPress={handleSubmit(_onSubmit)}
          title={{ value: strings.submit_info }}
          width={"100%"}
          style={{ ...css.selfCenter, ...css.marginY(5) }}
        />
      </View>
    </>
  );
}
